"use client";

import React from "react";
import { Box, Container, Typography, Button, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useColorScheme } from "@mui/material/styles";
import Link from "next/link";

const CTASection = styled(Box)<{ isDarkMode: boolean }>(
  ({ theme, isDarkMode }) => ({
    background: isDarkMode ? "#1a1a1a" : "#313132",
    color: "white",
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: isDarkMode
        ? 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%2342A6F6" fill-opacity="0.1"%3E%3Cpath d="M36 30c0-3.314-2.686-6-6-6s-6 2.686-6 6 2.686 6 6 6 6-2.686 6-6zm24 0c0-3.314-2.686-6-6-6s-6 2.686-6 6 2.686 6 6 6 6-2.686 6-6zM6 30c0-3.314-2.686-6-6-6s-6 2.686-6 6 2.686 6 6 6 6-2.686 6-6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        : 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Cpath d="M36 30c0-3.314-2.686-6-6-6s-6 2.686-6 6 2.686 6 6 6 6-2.686 6-6zm24 0c0-3.314-2.686-6-6-6s-6 2.686-6 6 2.686 6 6 6 6-2.686 6-6zM6 30c0-3.314-2.686-6-6-6s-6 2.686-6 6 2.686 6 6 6 6-2.686 6-6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    },
  })
);

const CTA: React.FC = () => {
  const { mode, systemMode } = useColorScheme();
  const isDarkMode =
    mode === "dark" || (mode === "system" && systemMode === "dark");

  return (
    <CTASection isDarkMode={isDarkMode}>
      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <Stack spacing={4} alignItems="center" textAlign="center">
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: "2rem", md: "2.5rem" },
              fontWeight: 600,
              mb: 2,
            }}
          >
            Ready to streamline your next receivership?
          </Typography>

          <Typography
            variant="h6"
            component="p"
            sx={{
              fontSize: { xs: "1.125rem", md: "1.25rem" },
              fontWeight: 400,
              opacity: 0.9,
              lineHeight: 1.6,
              maxWidth: "600px",
            }}
          >
            Join the growing number of insolvency professionals who are
            modernizing how they work.{" "}
            <strong>
              Early access means more input, more support, and more results.
            </strong>
          </Typography>

          <Link href="/contactUs" passHref style={{ textDecoration: "none" }}>
            <Button
              variant="outlined"
              sx={{
                backgroundColor: "white",
                borderColor: "white",
                color: "#42A6F6",
                fontWeight: 600,
                py: 1.5,
                px: 4,
                "&:hover": {
                  backgroundColor: "#42A6F6",
                  color: "white",
                  borderColor: "#42A6F6",
                },
                ...(isDarkMode && {
                  backgroundColor: "#42A6F6",
                  borderColor: "#42A6F6",
                  color: "#F7A56F",
                  fontWeight: 700,
                  "&:hover": {
                    backgroundColor: "#F49045",
                    borderColor: "#F49045",
                    color: "#1a1a1a",
                  },
                }),
              }}
            >
              Get started
            </Button>
          </Link>
        </Stack>
      </Container>
    </CTASection>
  );
};

export default CTA;
